import { type CSSProperties, useState } from "react";

import { arcPath } from "./chart-geometry";
import { ChartHoverTooltip } from "./chart-hover-tooltip";
import { cn } from "./cn";
import "./gauge.css";

export interface GaugeProps {
  label: string;
  /** 0..1, already clamped by the caller. */
  ratio: number;
  /** The number shown in the middle ("2.4%", "31%") — caller-formatted. */
  display: string;
  color: string;
  note?: string;
  /** Opt-in floating tooltip on hover (mouse only) restating the label,
   *  value, and note beside the cursor — see BarChart's `hoverTooltip` doc
   *  for why this stays opt-in rather than default-on. A gauge already shows
   *  its number inline, so this mostly buys UI CONSISTENCY with the other
   *  chart primitives on a dashboard that turns hover on everywhere; the
   *  `aria-label` already carries the same information for AT users
   *  regardless of this prop. */
  hoverTooltip?: boolean;
  className?: string;
}

/**
 * 240deg arc gauge for a bounded rate. The number IS the chart: a big
 * ink-colored value in the middle, the colored arc carries the magnitude.
 * A status gauge (error rate, say) switches `color` through good/warning/
 * danger thresholds at the call site and says so in `note` too — never color
 * alone.
 */
export function Gauge({
  label,
  ratio,
  display,
  color,
  note,
  hoverTooltip = false,
  className,
}: GaugeProps) {
  const [hovering, setHovering] = useState(false);
  const [anchor, setAnchor] = useState<{ x: number; y: number } | null>(null);
  const R = 54;
  const CX = 70;
  const CY = 70;
  // Angles run clockwise from 12 o'clock. 240 = bottom-left; a 240deg sweep
  // ends at 480 = 120 = bottom-right, so the gauge opens downward like a
  // speedometer.
  const START = 240;
  const SWEEP = 240;
  const clamped = Math.max(0, Math.min(ratio, 1));
  return (
    <figure
      className={cn("rc-gauge", className)}
      style={{ "--rc-chart-accent": color } as CSSProperties}
    >
      <svg
        className="rc-gauge__svg"
        viewBox="0 0 140 128"
        role="img"
        aria-label={`${label}: ${display}`}
        onPointerMove={(event) => {
          if (!hoverTooltip || event.pointerType !== "mouse") return;
          setHovering(true);
          setAnchor({ x: event.clientX, y: event.clientY });
        }}
        onPointerLeave={(event) => {
          if (!hoverTooltip || event.pointerType !== "mouse") return;
          setHovering(false);
        }}
      >
        <path className="rc-gauge__track" d={arcPath(CX, CY, R, START, START + SWEEP)} />
        {clamped > 0 ? (
          <path
            className="rc-gauge__fill"
            d={arcPath(CX, CY, R, START, START + SWEEP * Math.max(clamped, 0.02))}
          />
        ) : null}
        <text className="rc-gauge__value" x={CX} y={CY + 6} textAnchor="middle">
          {display}
        </text>
      </svg>
      <figcaption className="rc-gauge__label">
        {label}
        {note ? <span className="rc-gauge__note">{note}</span> : null}
      </figcaption>
      {hovering && anchor ? (
        <ChartHoverTooltip anchor={anchor}>
          <span className="rc-chart-hover-tooltip__row">
            <span
              className="rc-chart-hover-tooltip__swatch"
              aria-hidden="true"
              style={{ background: color }}
            />
            <span className="rc-chart-hover-tooltip__label">{label}</span>
            <span className="rc-chart-hover-tooltip__value">{display}</span>
          </span>
          {note ? <span className="rc-chart-hover-tooltip__label">{note}</span> : null}
        </ChartHoverTooltip>
      ) : null}
    </figure>
  );
}
